// Partes visuais do PDF Edit (0.50): miniaturas das páginas, organizar arrastando, tarjas pretas e leitor.
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Image, PanResponder, Pressable, Text, View, useWindowDimensions } from 'react-native';
import { Ordenavel } from './Arrastar';
import { C, alpha } from './theme';
import { renderizar, temRenderizador } from '../lib/pdfRender';
import type { Tarja } from '../lib/pdfEdit';
import { t } from '../lib/i18n';

/** Miniatura de uma página (começa em 0). */
export function PaginaPdf({ uri, i, larg, giro = 0, cor, maxLado = 500 }: { uri: string; i: number; larg: number; giro?: number; cor: string; maxLado?: number }) {
  const [img, setImg] = useState<{ uri: string; w: number; h: number } | null>(null);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    let vivo = true;
    if (temRenderizador()) renderizar(uri, i, maxLado, 70).then(r => { if (vivo) setImg(r); }).catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; };
  }, [uri, i, maxLado]);
  const deitada = giro % 180 !== 0;
  const prop = img ? (deitada ? img.w / img.h : img.h / img.w) : 1.41;
  const alt = Math.round(larg * Math.min(1.6, Math.max(0.5, prop)));
  return (
    <View style={{ width: larg, height: alt, borderRadius: 6, overflow: 'hidden', backgroundColor: '#fff', borderWidth: 1, borderColor: C.line, alignItems: 'center', justifyContent: 'center' }}>
      {img ? (
        <Image source={{ uri: img.uri }} resizeMode="contain"
          style={{ width: deitada ? alt : larg, height: deitada ? larg : alt, transform: [{ rotate: `${giro}deg` }] }} />
      ) : !temRenderizador() || erro ? <Text style={{ color: '#555', fontSize: 18, fontWeight: '700' }}>{i + 1}</Text> : <ActivityIndicator color={cor} />}
    </View>
  );
}

// ─── Organizar páginas: arrastar, girar, duplicar, apagar e página em branco ───
type ItemOrg = { k: string; p: number | null; giro: number };
export function OrganizarPaginas({ uri, total, cor, onMudar }: { uri: string; total: number; cor: string; onMudar: (o: { p: number | null; giro: number }[]) => void }) {
  const { width } = useWindowDimensions();
  const [itens, setItens] = useState<ItemOrg[]>(() => Array.from({ length: total }, (_, i) => ({ k: 'p' + i, p: i, giro: 0 })));
  const seq = useRef(0);
  useEffect(() => { onMudar(itens.map(x => ({ p: x.p, giro: x.giro }))); }, [itens]); // eslint-disable-line react-hooks/exhaustive-deps
  const col = width > 700 ? 5 : 3;
  const larg = Math.floor((Math.min(width, 900) - 32 - (col - 1) * 10) / col);
  const mudar = (k: string, fn: (x: ItemOrg) => ItemOrg | ItemOrg[] | null) =>
    setItens(l => l.flatMap(x => { if (x.k !== k) return [x]; const r = fn(x); return r == null ? [] : Array.isArray(r) ? r : [r]; }));
  return (
    <View style={{ gap: 10 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Segure e arraste para mudar a ordem. ↻ gira, ⧉ duplica e ✕ tira a página.')}</Text>
      <Ordenavel itens={itens} chave={x => x.k} grade gap={10} onOrdem={setItens} estiloItem={() => ({ width: larg })}
        render={(x, i) => (
          <View style={{ gap: 4, alignItems: 'center' }}>
            {x.p == null
              ? <View style={{ width: larg, height: larg * 1.41, borderRadius: 6, backgroundColor: '#fff', borderWidth: 1, borderColor: C.line, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: '#888', fontSize: 12 }}>{t('Em branco')}</Text></View>
              : <PaginaPdf uri={uri} i={x.p} larg={larg} giro={x.giro} cor={cor} maxLado={400} />}
            <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center' }}>
              <Text style={{ color: C.sub, fontSize: 12 }}>{i + 1}</Text>
              <Pressable hitSlop={8} onPress={() => mudar(x.k, y => ({ ...y, giro: (y.giro + 90) % 360 }))}><Text style={{ color: C.text, fontSize: 16 }}>↻</Text></Pressable>
              <Pressable hitSlop={8} onPress={() => mudar(x.k, y => [y, { ...y, k: 'd' + ++seq.current }])}><Text style={{ color: C.text, fontSize: 16 }}>⧉</Text></Pressable>
              <Pressable hitSlop={8} onPress={() => mudar(x.k, () => null)}><Text style={{ color: C.danger, fontSize: 16 }}>✕</Text></Pressable>
            </View>
          </View>
        )} />
      <Pressable onPress={() => setItens(l => [...l, { k: 'b' + ++seq.current, p: null, giro: 0 }])}
        style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: C.line, borderRadius: 14, padding: 14, alignItems: 'center' }}>
        <Text style={{ color: C.sub }}>+ {t('Página em branco no fim')}</Text>
      </Pressable>
    </View>
  );
}

// ─── Tarjas pretas: arraste o dedo sobre o que quer esconder ───
export function DesenharTarjas({ uri, total, cor, tarjas, onMudar }: { uri: string; total: number; cor: string; tarjas: Tarja[]; onMudar: (t: Tarja[]) => void }) {
  const { width } = useWindowDimensions();
  const [pg, setPg] = useState(0);
  const [img, setImg] = useState<{ uri: string; w: number; h: number } | null>(null);
  const [novo, setNovo] = useState<Tarja | null>(null);
  const larg = Math.min(width - 32, 700);
  useEffect(() => { setImg(null); renderizar(uri, pg, 1400, 80).then(setImg).catch(() => {}); }, [uri, pg]);
  const alt = img ? (larg * img.h) / img.w : larg * 1.41;
  const ref = useRef({ larg, alt, pg, tarjas, onMudar }); ref.current = { larg, alt, pg, tarjas, onMudar };
  const ini = useRef({ x: 0, y: 0 });
  const pan = useRef(PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: e => { ini.current = { x: e.nativeEvent.locationX, y: e.nativeEvent.locationY }; },
    onPanResponderMove: (_, g) => {
      const { larg: L, alt: A, pg: P } = ref.current;
      const x0 = ini.current.x, y0 = ini.current.y, x1 = x0 + g.dx, y1 = y0 + g.dy;
      setNovo({ pagina: P, x: Math.max(0, Math.min(x0, x1)) / L, y: Math.max(0, Math.min(y0, y1)) / A, w: Math.abs(g.dx) / L, h: Math.abs(g.dy) / A });
    },
    onPanResponderRelease: () => {
      setNovo(n => { if (n && n.w > 0.01 && n.h > 0.005) ref.current.onMudar([...ref.current.tarjas, n]); return null; });
    },
  })).current;
  const daPagina = tarjas.map((r, k) => ({ r, k })).filter(x => x.r.pagina === pg);
  return (
    <View style={{ gap: 10 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Arraste o dedo sobre o que quer esconder. Toque numa tarja para tirar.')}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <Pressable disabled={pg === 0} onPress={() => setPg(p => p - 1)}><Text style={{ color: pg ? C.text : C.mute, fontSize: 22 }}>‹</Text></Pressable>
        <Text style={{ color: C.text }}>{t('Página {n} de {total}', { n: pg + 1, total })}</Text>
        <Pressable disabled={pg >= total - 1} onPress={() => setPg(p => p + 1)}><Text style={{ color: pg < total - 1 ? C.text : C.mute, fontSize: 22 }}>›</Text></Pressable>
      </View>
      <View style={{ width: larg, height: alt, alignSelf: 'center', backgroundColor: '#fff', borderRadius: 6, overflow: 'hidden' }}>
        {img ? <Image source={{ uri: img.uri }} style={{ width: larg, height: alt }} /> : <ActivityIndicator color={cor} style={{ marginTop: 60 }} />}
        <View {...pan.panHandlers} style={{ position: 'absolute', left: 0, top: 0, width: larg, height: alt }}>
          {daPagina.map(({ r, k }) => (
            <Pressable key={k} onPress={() => onMudar(tarjas.filter((_, j) => j !== k))}
              style={{ position: 'absolute', left: r.x * larg, top: r.y * alt, width: r.w * larg, height: r.h * alt, backgroundColor: '#000' }} />
          ))}
          {novo && <View pointerEvents="none" style={{ position: 'absolute', left: novo.x * larg, top: novo.y * alt, width: novo.w * larg, height: novo.h * alt, backgroundColor: alpha('#000', 0.7), borderWidth: 1, borderColor: cor }} />}
        </View>
      </View>
      <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center' }}>{tarjas.length === 1 ? t('1 tarja no documento') : t('{n} tarjas no documento', { n: tarjas.length })}</Text>
    </View>
  );
}

// ─── Leitor: todas as páginas, uma embaixo da outra ───
export function LeitorPdf({ uri, total, cor }: { uri: string; total: number; cor: string }) {
  const { width } = useWindowDimensions();
  const larg = Math.min(width - 24, 900);
  return (
    <FlatList data={Array.from({ length: total }, (_, i) => i)} keyExtractor={i => String(i)} initialNumToRender={2} windowSize={5}
      contentContainerStyle={{ padding: 12, gap: 12, paddingBottom: 60 }}
      renderItem={({ item }) => (
        <View style={{ alignItems: 'center', gap: 4 }}>
          <PaginaPdf uri={uri} i={item} larg={larg} cor={cor} maxLado={Math.round(larg * 2)} />
          <Text style={{ color: C.mute, fontSize: 11 }}>{item + 1} / {total}</Text>
        </View>
      )} />
  );
}
